import { type TrackModel } from '@/shared/api'
import { TABLE_PAGE_SIZE_OPTIONS } from '@/shared/lib/query'
import { TableWrapper } from '@/shared/ui'
import { Table, type TableProps } from 'antd'
import { useUnit } from 'effector-react'

import { type TracksSortState } from '../../lib/tracks-query-model'
import {
  $tracksTablePage,
  $tracksTablePageSize,
  $tracksTableSelectedRowKeys,
  $tracksTableSort,
  tracksTablePageChanged,
  tracksTablePageReset,
  tracksTablePageSizeChanged,
  tracksTableQuery,
  tracksTableSelectedRowKeysChanged,
  tracksTableSortChanged,
  tracksTableSortReset,
} from '../../model/tracks-table.model'
import { useTracksTableColumns } from './useTracksTableColumns'

interface TracksTableProps {
  onEdit: (trackId: string) => void
}
export const TracksTable = ({ onEdit }: TracksTableProps) => {
  const { data, pending } = useUnit(tracksTableQuery)

  const sort = useUnit($tracksTableSort)
  const changeSort = useUnit(tracksTableSortChanged)
  const resetSort = useUnit(tracksTableSortReset)

  const page = useUnit($tracksTablePage)
  const changePage = useUnit(tracksTablePageChanged)
  const resetPage = useUnit(tracksTablePageReset)
  const pageSize = useUnit($tracksTablePageSize)
  const changePageSize = useUnit(tracksTablePageSizeChanged)

  const [selectedRowKeys, changeSelectedRowKeys] = useUnit([
    $tracksTableSelectedRowKeys,
    tracksTableSelectedRowKeysChanged,
  ])

  const tableColumns: TableProps<TrackModel>['columns'] = useTracksTableColumns(
    { onEdit, sort },
  )

  if (!data?.items || !data.items.length) {
    return null
  }

  const onTableChange: TableProps<TrackModel>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    // Sort
    if (!Array.isArray(sorter)) {
      const field = sorter.field as string | undefined
      const order = sorter.order as 'ascend' | 'descend' | undefined

      if (field && order) {
        const sortOrder = order === 'ascend' ? 'asc' : 'desc'

        if (sort.field !== field || sort.order !== sortOrder) {
          changeSort({
            field: field as TracksSortState['field'],
            order: sortOrder,
          })
          resetPage()
        }
      } else if (sort.field !== null || sort.order !== null) {
        resetSort()
        resetPage()
      }
    }
    // Pagination
    if (pagination.current && pagination.pageSize) {
      if (pagination.current !== page) {
        changePage(pagination.current)
      }
      if (pagination.pageSize !== pageSize) {
        changePageSize(pagination.pageSize)
        resetPage()
      }
    }
  }

  const rowSelection: TableProps<TrackModel>['rowSelection'] = {
    type: 'checkbox',
    selectedRowKeys,
    columnWidth: 50,
    fixed: true,
    onChange: changeSelectedRowKeys,
  }

  return (
    <TableWrapper>
      <Table
        sticky
        showSorterTooltip={false}
        rowKey={(record) => record.id}
        bordered
        rowSelection={rowSelection}
        columns={tableColumns}
        dataSource={data.items}
        loading={pending}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data.totalItems,
          showSizeChanger: true,
          pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
          locale: { items_per_page: 'Треков на странице' },
        }}
      />
    </TableWrapper>
  )
}
